import React, { useState, useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import { useUser } from "@/contexts/UserContext";
import {alert as byzzerAlert, ByzzerLink, ByzzerModal, openModal, useModals} from '@/components/form';
import { useTenantApi } from "@/hooks/useTenantApi";
import { useBetterNavigate } from "@/utils";
import { CreditUsage } from "@/types/ApiTypes";
import { NavigationAnalyticsType } from "@/types/AnalyticsTypes";

export type useCanRunReportProps = {
    changeReport?: boolean;
    baseClassName: string;
    onChangeReport?: (reportType: string, sku: string) => void;
    validateOnly?: boolean;
}

const messages = {
    noRunsLeft: "Your team does not have any run counts left for this type of report. Ask your admin to allocate runs in order to use this report.",
    creditsUsed: "You have used all remaining purchased credits for the selected report."
};

export const useCanRunReport = ({
    changeReport = false,
    baseClassName = '',
    onChangeReport,
}: useCanRunReportProps) => {
    const { user, admins } = useUser();
    const { debouncedGetMySubscriptionUsage } = useTenantApi();
    const [canRunReports, setCanRunReports] = useState(false);
    const navigate = useBetterNavigate();

    const checkUserPermissions = (validateOnly: boolean | undefined) => {
        if (!canRunReports && user?.role === 'viewer') {
            if(!validateOnly)
            showViewerAccessModal();
            return false;
        }
        return canRunReports;
    };

    const showViewerAccessModal = () => {
        openModal({
            closeOnClickOutside: true,
            title: "Sorry! You don't have access to do that.",
            content: (
                <>
                    <p>As a viewer, you don't have access to run a new report. </p>
                    <p>Reach out to your account admin
                        <div className={`${baseClassName}__admins-view`}>
                            {admins.map((admin) => (
                                <p key={admin.email}>
                                    <span>{` ${admin.firstName} `}</span>
                                    <span>{` ${admin.lastName} `}</span>
                                    <span><a className='byzzer-link'
                                        href={`mailto:${admin.email}`}>{` ( ${admin.email} ) `}</a></span>
                                </p>
                            ))}
                        </div>
                        to upgrade your member type to user.
                    </p>
                </>
            ),
            className: `${baseClassName}__viewer-access-modal`,
            headerType: 'normal'
        });
    };

    const showReportAvailabilityAlert = (messageType: keyof typeof messages) => {
        
        byzzerAlert({
            // @ts-expect-error - byzzerAlert is not typed yet, it's old code
            onResolve: () => {},
            content: <p>{messages[messageType]}</p>
        });
    };

    const checkReportAvailability = (reportInfo: CreditUsage, validateOnly: boolean | undefined) => {
        if (!reportInfo || reportInfo.limit === 0) {
            if(!validateOnly)
            showReportAvailabilityAlert('noRunsLeft');
            return false;
        }
        
        if (reportInfo.limit - reportInfo.used <= 0) {
            if(!validateOnly)
            showReportAvailabilityAlert('creditsUsed');
            return false;
        }
        
        return true;
    };


    const navigateToReportConfig = (
        sku: string, 
        analytics?: NavigationAnalyticsType
    ) => {
        navigate(`/dashboard/configure_report`, {
            params: {
                runType: 'subscription',
                sku,
            },
            state: {
                analytics: analytics ? analytics : {}
            },
        });
    };

     // todo: break this function up into the checking and then the navigation
    const validateReportCountAndShow = async (
        { reportType, sku }: { reportType?: string; sku: string; },
        analytics?: NavigationAnalyticsType,
        validateOnly?: boolean
    ) => {
        // Check user permissions
        if (!reportType ||!checkUserPermissions(validateOnly)) {
            return;
        }
        
        // Handle change report mode
        if (changeReport) {
            onChangeReport?.('subscription', sku);
            return;
        }
        
        // Check report availability and navigate if possible
        // setLoading(true);
        try {
            const {premiumReports, basicReports} = await debouncedGetMySubscriptionUsage();
            
            // Determine which report type to check
            const reportToCheck = reportType === 'smart' ? premiumReports : basicReports;
            
            // Check availability and navigate if allowed
            if (checkReportAvailability(reportToCheck, validateOnly)) {
                if(validateOnly)
                return true;
                navigateToReportConfig(sku, analytics);
            }
        } finally {
            // setLoading(false);
        }
    };


    useEffect(() => {
        if (user?.role) {
            setCanRunReports(['user', 'admin'].includes(user?.role));
        }
    }, [user]);

    return validateReportCountAndShow;

};

export default useCanRunReport;

useCanRunReport.displayName = 'useCanRunReport';